import { useCallback, useState } from 'react';

import useUpdateEffect from 'hooks/useUpdateEffect';

import { faImageSlash } from '@fortawesome/pro-light-svg-icons';

import Icon from './Icon';
import Skeleton from './Skeleton';
import * as S from './styled/Image.styled';

/**
 * @description Image component with lazy loading and error handling
 * @param {object} props
 * @param {string} props.url - image source
 * @param {string} props.defaultUrl - default image source
 * @param {string} props.alt - image alt
 * @param {string} props.className - image className
 * @param {object} props.style - image style
 * @param {string} props.width - image width
 * @param {string} props.height - image height
 * @example
 * <Image
	url="https://i.pinimg.com/originals/7c/11/8e/7c118e077c2a9a030343b9a47473e7b7.jpg"
	alt="hamburger"
	height="10rem"
	width="10rem"
	className=""
	style={{}}
	/>
 */

const Image = (props) => {
	const { url = '', defaultUrl = '', afterError, width = '5rem', height = '5rem', ...restProps } = props;

	const [imageURL, setImageURL] = useState(url ?? defaultUrl);
	const [failed, setFailed] = useState(!url && !defaultUrl);
	const [loading, setLoading] = useState(!!url || !!defaultUrl);

	useUpdateEffect(() => {
		const newUrl = url ?? defaultUrl;
		if (newUrl !== imageURL) {
			setImageURL(newUrl);
			setFailed(!url && !defaultUrl);
			setLoading(!!url || !!defaultUrl);
		}
	}, [url, defaultUrl]);

	const onError = useCallback(() => {
		if (defaultUrl && imageURL !== defaultUrl) {
			setImageURL(defaultUrl);
		} else {
			setFailed(true);
			setLoading(false);
		}
		afterError?.();
	}, [imageURL, defaultUrl, afterError]);

	return (
		<>
			{failed ? (
				<S.FailedImageContainer width={width} height={height}>
					<Icon icon={faImageSlash} size="large" color="var(--black-60)" />
				</S.FailedImageContainer>
			) : (
				<>
					{loading && <Skeleton width={width} height={height} />}
					<S.ImageRoot
						key={imageURL}
						src={imageURL}
						onError={onError}
						onLoad={() => setLoading(false)}
						width={width}
						height={height}
						display={loading ? 'none' : 'block'}
						{...restProps}
					/>
				</>
			)}
		</>
	);
};

export default Image;
